import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import Name from "_shared/names/name";
import NameSchema from "_shared/names/name-schema";
import { z } from "zod";

export type UserProfileJSON = z.infer<typeof UserProfileSchema.UserProfileJSONZ>;
export type UserProfileParams = z.infer<typeof UserProfileSchema.UserProfileZ>;

export default class UserProfileSchema {
  public static readonly UserProfileJSONZ = z.object({
    id: z.uuid(),
    firstName: NameSchema.NameJSONZ.shape.firstName,
    lastName: NameSchema.NameJSONZ.shape.lastName,
    email: z.email().or(z.literal("")).nullish(),
    isClientRep: z.boolean(),
    isVendorRep: z.boolean(),
  }).refine((data) => data.firstName !== undefined || data.lastName !== undefined, {
    message: "Either firstName or lastName must be provided",
  });

  public static readonly UserProfileZ = z.object({
    id: z.instanceof(Guid),
    name: z.instanceof(Name),
    email: z.instanceof(EmailAddress).nullish(),
    isClientRep: z.boolean(),
    isVendorRep: z.boolean(),
  });

  public static validate(values: unknown): UserProfileParams {
    return parseOrThrow(UserProfileSchema.UserProfileZ, values, {
      logError: true,
      schemaName: "UserProfileSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserProfileJSON {
    return parseOrThrow(UserProfileSchema.UserProfileJSONZ, json, {
      logError: true,
      schemaName: "UserProfileSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserProfileParams {
    try {
      UserProfileSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserProfileJSON {
    try {
      UserProfileSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: UserProfileJSON): UserProfileParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(UserProfileSchema.UserProfileJSONZ, json, {
      logError: true,
      schemaName: "UserProfileSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserProfileSchema.codec.decode(parsed);
  }

  public static serialize(UserProfile: UserProfileParams): UserProfileJSON {
    if (isNullOrUndefined(UserProfile)) return UserProfile;
    const parsed = UserProfileSchema.validate(UserProfile);
    if (isNullOrUndefined(parsed)) return parsed;
    return UserProfileSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(UserProfileSchema.UserProfileJSONZ, UserProfileSchema.UserProfileZ, {
    decode: (data) => ({
      id: new Guid(GuidSchema.deserialize(data.id)),
      name: new Name(NameSchema.deserialize(data)),
      email: EmailAddress.fromJSON(data.email) ?? undefined,
      isClientRep: data.isClientRep,
      isVendorRep: data.isVendorRep,
    }),
    encode: (profile) => {
      const { firstName, lastName } = profile.name?.toJSON() ?? {};
      return {
        id: profile.id.toJSON(),
        firstName: firstName,
        lastName: lastName,
        email: profile.email?.toJSON(),
        isClientRep: profile.isClientRep,
        isVendorRep: profile.isVendorRep,
      };
    },
  });
}
